import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Col, DatePicker, Layout, Row, Select } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { omit, startCase } from 'lodash';
import queryString from 'query-string';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

import CustomerServices from 'services/customer';

import PdButton from 'components/pd-button';
import PdCsvDownloader from 'components/pd-csv-downloader';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';

import { convertNumberToRupiahCurrency, DATE_FORMAT, errHandling } from 'utils';

const { Content } = Layout;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CUSTOMER_WALLET_ACTIVITY = ['cashback', 'refund', 'payment', 'disburse'];

export function CustomerWallet() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [activity, setActivity] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [endDate, setEndDate] = useState('');
  const [loadStatus, setLoadStatus] = useState(true);
  const [nextPage, setNextPage] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [walletList, setWalletList] = useState([]);

  const navigate = useNavigate();
  const parsed = queryString.parse(location.search);


  const buttonAction = ({ customer: { id } } = {}) => (
    <Row className="button-table">
      <Col span={24} className="button">
        <PdButton
          className="height-32"
          type="primary"
          full
          onClick={handleToCustomerDetail.bind(this, id)}>
          See Detail
        </PdButton>
      </Col>
    </Row>
  );

  const columns = [
    {
      name: 'Date',
      selector: row => row.created_at,
      width: '10%',
      format: (row) => dayjs(row.created_at).format(DATE_FORMAT),
      center: true,
    },
    {
      name: 'Customer Name',
      selector: row => row.customer.first_name,
      width: '14%',
      center: true,
    },
    {
      name: 'Email',
      selector: row => row.customer.email,
      width: '20%',
      center: true,
    },
    {
      name: 'Phone number',
      selector: row => row.customer.phone_number,
      width: '15%',
      center: true,
    },
    {
      name: 'Activity',
      selector: row => row.activity,
      width: '10%',
      center: true,
    },
    {
      name: 'Amount',
      selector: row => row.amount,
      width: '15%',
      format: (row) => convertNumberToRupiahCurrency(row.amount),
      center: true,
    },
    {
      name: '',
      cell: buttonAction,
      width: '16%',
    },
  ];

  function handleToCustomerDetail(customerId) {
    navigate({
      pathname: '/customers/detail',
      search: `?id=${customerId}&from=wallet`,
    });
  }

  async function fetchCustomerWallet({
    activity,
    end_date,
    page,
    start_date,
  } = {}) {
    setLoadStatus(true);
    try {
      const {
        data: { data = [], metadata: { next_cursor = -1 } = {} },
      } = await CustomerServices.getCustomerGlobalWallet({
        activity,
        cursor: page,
        end_date,
        limit: 10,
        start_date,
      });
      setWalletList(data);
      setNextPage(next_cursor);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  function changePage(page) {
    if (page < 1) page = 1;
    parsed['page'] = page;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/customer-wallet',
      search: stringified,
    });
  }

  function resetFilter() {
    const stringified = queryString.stringify(
      omit(parsed, ['limit', 'page', 'activity', 'start_date', 'end_date'])
    );
    navigate({
      pathname: '/customer-wallet',
      search: stringified,
    });
  }

  function filterWallet(param, val) {
    if (val === '' || val === 0) {
      delete parsed[param];
    } else {
      parsed[param] = val;
    }
    parsed['page'] = 1;
    const stringified = queryString.stringify(parsed);
    navigate({
      pathname: '/customer-wallet',
      search: stringified,
    });
  }

  function handleChangeDate(val) {
    const [start = '', end = ''] = val;
    const startDate = start && dayjs(start).format('MM/DD/YYYY');
    const endDate = end && dayjs(end).format('MM/DD/YYYY');
    filterWallet('start_date', startDate);
    filterWallet('end_date', endDate);
  }

  async function handleDownloadCsv() {
    const {
      data: { data = [] },
    } = await CustomerServices.getGlobalCustomerWalletReport();
    console.info(data)
      return data
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* eslint-disable */
  useEffect(() => {
    if (parsed['page']) {
      if (parsed['page'] < 1) {
        parsed['page'] = 1;
        const stringified = queryString.stringify(parsed);
        navigate({
          pathname: '/customer-wallet',
          search: stringified,
        });
      } else setCurrentPage(parsed['page']);
    } else setCurrentPage(1);

    if (parsed['activity']) setActivity(parsed['activity']);
    else setActivity(undefined);

    if (parsed['start_date']) setStartDate(parsed['start_date']);
    else setStartDate('');

    if (parsed['end_date']) setEndDate(parsed['end_date']);
    else setEndDate('');

    fetchCustomerWallet({
      activity: parsed['activity'],
      end_date: parsed['end_date'] && `${parsed['end_date']} 23:59`,
      page: parsed['page'],
      start_date: parsed['start_date'],
    });
  }, [
    parsed['activity'],
    parsed['end_date'],
    parsed['page'],
    parsed['start_date'],
  ]);
  /* eslint-enable */

  return (
    <Layout className="main-container pd-cms-customer-wallet">
      <div className="content">
        <div className="pageTitle">Wallet Activity</div>
        <Content className="pageContent">
          <Row className="pd-margin-bottom-md">
            <Col lg={7}>
              <RangePicker
                className="w-full"
                onChange={handleChangeDate}
                format={DATE_FORMAT}
                value={[
                  startDate && dayjs(startDate),
                  endDate && dayjs(endDate),
                ]}
              />
            </Col>
            <Col lg={{ span: 3, offset: 1 }}>
              <Select
                className="w-full"
                placeholder="Activity"
                onChange={filterWallet.bind(this, 'activity')}
                value={activity}>
                {CUSTOMER_WALLET_ACTIVITY.map((activity, idx) => (
                  <Option key={idx} value={activity}>
                    {startCase(activity)}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col lg={{ span: 4, offset: 1 }}>
              <span className="reset-filter" onClick={resetFilter}>
                Reset Filter
              </span>
            </Col>
            <Col lg={{ span: 4, offset: 3 }}>
              <PdCsvDownloader
                filename="customer-wallet-homestory"
                datas={handleDownloadCsv}
              />
            </Col>
          </Row>
          <Row>
            {!loadStatus && (
              <TableList
                changePage={changePage}
                columns={columns}
                loadingOrder={loadStatus}
                nextPage={nextPage}
                orderList={walletList}
                page={currentPage}
                responsive={false}
              />
            )}
          </Row>
        </Content>
      </div>
    </Layout>
  );
}

export default { CustomerWallet };
